/**
 * User administration (KAN-46, `users.manage`).
 *
 * Adding someone here is what grants access: a firm Google account with no row
 * in this table is refused at sign-in. There is no credential to issue or hand
 * over — the person signs in with the Google account they already have.
 *
 * Every control is also a rule on the server: an administrator cannot change
 * their own role or deactivate themselves, and the API refuses both regardless
 * of what this screen renders. The controls are disabled anyway, because a
 * button that always fails is worse than no button.
 */
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROLES, ROLE_LABELS, type Role } from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { domainLabel, useAuthConfig } from '../lib/authConfig'
import { useSession } from '../lib/session'
import { formatDateTime, formatRelative } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, RoleBadge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'
import { PlusIcon } from '../ui/icons'

interface AdminUser {
  id: string
  fullName: string
  email: string
  role: Role
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
}

export function Users() {
  const { user: self } = useSession()
  const queryClient = useQueryClient()
  const config = useAuthConfig()

  const [adding, setAdding] = useState(false)
  const [granted, setGranted] = useState<AdminUser | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<{ users: AdminUser[] }>('/users'),
  })

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const create = useMutation({
    mutationFn: (input: { email: string; role: Role }) =>
      apiFetch<{ user: AdminUser }>('/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: async (result) => {
      setAdding(false)
      setGranted(result.user)
      await invalidate()
    },
  })

  const update = useMutation({
    mutationFn: ({ id, ...patch }: { id: string; role?: Role; isActive?: boolean }) =>
      apiFetch<{ user: AdminUser }>(`/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onMutate: () => setRowError(null),
    onSuccess: invalidate,
    onError: (error) => setRowError(messageFor(error)),
  })

  const rows = users.data?.users ?? []
  const busyId = update.isPending ? update.variables?.id : null

  return (
    <>
      <PageHeader
        title="Users"
        description="Who can sign in, and what each of them may see. Sign-in is a firm Google account plus a row here — one without the other is refused."
        actions={
          <Button variant="primary" onClick={() => setAdding((open) => !open)}>
            <PlusIcon />
            Add account
          </Button>
        }
      />

      {granted && (
        <Alert tone="success" className="mb-6" title={`${granted.email} now has access`}>
          <p>
            They can sign in at this address with that Google account. There is no password to send,
            and their name will fill in from their Google profile the first time they do.
          </p>
          <div className="mt-3">
            <Button size="sm" variant="ghost" onClick={() => setGranted(null)}>
              Done
            </Button>
          </div>
        </Alert>
      )}

      {adding && (
        <NewAccountForm
          allowedDomains={config.data?.allowedDomains ?? null}
          submitting={create.isPending}
          error={create.error ? messageFor(create.error) : null}
          onCancel={() => setAdding(false)}
          onSubmit={(input) => create.mutate(input)}
        />
      )}

      {rowError && (
        <Alert tone="error" className="mb-6">
          {rowError}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Accounts"
          description={
            users.isPending
              ? 'Loading…'
              : `${rows.filter((row) => row.isActive).length} active of ${rows.length}`
          }
        />

        {users.error ? (
          <CardBody>
            <Alert tone="error">{messageFor(users.error)}</Alert>
          </CardBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Last sign-in</Th>
                <Th className="text-right">Access</Th>
              </tr>
            </thead>
            <tbody>
              {users.isPending && <EmptyRow colSpan={5}>Loading accounts…</EmptyRow>}

              {!users.isPending && rows.length === 0 && (
                <EmptyRow colSpan={5}>No accounts yet.</EmptyRow>
              )}

              {rows.map((row) => {
                const isSelf = row.id === self?.id
                const busy = busyId === row.id

                return (
                  <tr key={row.id} className={row.isActive ? undefined : 'bg-ink-50/60'}>
                    <Td>
                      <span className="block font-medium text-ink-900">
                        {row.fullName}
                        {isSelf && <span className="ml-2 text-xs text-ink-500">(you)</span>}
                      </span>
                      <span className="block text-xs text-ink-600">{row.email}</span>
                    </Td>

                    <Td>
                      {isSelf ? (
                        /**
                         * Rendered as a badge rather than a disabled select: the
                         * point is not "you may change this later", it is that
                         * your own role is not yours to change.
                         */
                        <RoleBadge role={row.role} />
                      ) : (
                        <Select
                          aria-label={`Role for ${row.fullName}`}
                          className="mt-0 max-w-48"
                          value={row.role}
                          disabled={busy}
                          onChange={(event) =>
                            update.mutate({ id: row.id, role: event.target.value as Role })
                          }
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Td>

                    <Td>
                      <Badge tone={row.isActive ? 'positive' : 'neutral'}>
                        {row.isActive ? 'Active' : 'Deactivated'}
                      </Badge>
                    </Td>

                    <Td className="whitespace-nowrap">
                      <span className="block text-ink-800">{formatRelative(row.lastLoginAt)}</span>
                      <span className="block text-xs text-ink-500">
                        Added {formatDateTime(row.createdAt)}
                      </span>
                    </Td>

                    <Td>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant={row.isActive ? 'danger' : 'secondary'}
                          disabled={isSelf || busy}
                          title={isSelf ? 'You cannot deactivate your own account' : undefined}
                          onClick={() => update.mutate({ id: row.id, isActive: !row.isActive })}
                        >
                          {row.isActive ? 'Revoke access' : 'Restore access'}
                        </Button>
                      </div>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <p className="mt-4 max-w-3xl text-xs leading-relaxed text-ink-600">
        Accounts are deactivated rather than deleted. Audit rows reference the account that made each
        change, and that history is append-only — removing the account would leave it unattributable.
        Revoking access drops any session the account is holding immediately; their Google account is
        untouched, since it is this platform's access that ends, not their firm login.
      </p>
    </>
  )
}

function NewAccountForm({
  allowedDomains,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  /** Null until the config request lands; the copy simply says less until then. */
  allowedDomains: string[] | null
  submitting: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (input: { email: string; role: Role }) => void
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('executive')

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    onSubmit({ email: email.trim(), role })
  }

  return (
    <Card className="mb-6">
      <CardHeader
        title="Add an account"
        description="An address and a role. Nothing is emailed and no password is created — they sign in with Google as soon as this is saved, and their name arrives with them."
      />
      <CardBody>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="new-email"
              label="Google account"
              hint={
                allowedDomains?.length
                  ? `Must be an ${domainLabel(allowedDomains)} address — no other domain can sign in.`
                  : 'Must be a firm Google account; no other domain can sign in.'
              }
            >
              <TextInput
                id="new-email"
                type="email"
                required
                autoFocus
                autoComplete="off"
                placeholder={allowedDomains?.length ? `name@${allowedDomains[0]}` : undefined}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field
              id="new-role"
              label="Role"
              hint={
                <Link className="text-brand-700 underline" to="/roles">
                  What each role may do
                </Link>
              }
            >
              <Select
                id="new-role"
                value={role}
                onChange={(event) => setRole(event.target.value as Role)}
              >
                {ROLES.map((option) => (
                  <option key={option} value={option}>
                    {ROLE_LABELS[option]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {error && (
            <Alert tone="error" className="mt-4">
              {error}
            </Alert>
          )}

          <div className="mt-5 flex items-center gap-2">
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Granting…' : 'Grant access'}
            </Button>
            <Button type="button" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status === 422 || error.status === 409
      ? error.message
      : `${error.message} (${error.status})`
  }
  return 'Something went wrong. Try again.'
}
